'use client';

import { useMemo } from 'react';
import { AssetTier, MarketStatus } from '@velocity-exchange/sdk';
import { ENUM_UTILS } from '@velocity-exchange/common';
import Button from 'src/components/primitives/Button/Button';
import useModal from 'src/components/Modals/hooks/useModal';
import useVelocityClient from 'src/hooks/useVelocityClient';
import useTickedAllSubAccounts from 'src/hooks/useTickedAllSubAccounts';
import { useBalanceRowData } from 'src/hooks/useBalanceRowData';
import { BalanceRowData } from './types';
import { usePrivyWalletContext } from 'src/providers/wallets/PrivyWalletProvider';

interface ActionsCellProps {
	row: BalanceRowData;
}

export function ActionsCell({ row }: ActionsCellProps) {
	const { spotMarketIndex, highlighted } = row;
	const { show } = useModal();
	const velocityClient = useVelocityClient();
	const subaccounts = useTickedAllSubAccounts();
	const { isPrivyNativeAuth } = usePrivyWalletContext();

	const spotMarketInfo = useBalanceRowData({
		spotMarketIndex,
		highlighted,
	});

	const spotMarketAccount = useMemo(
		() => velocityClient?.getSpotMarketAccount(spotMarketIndex),
		[velocityClient, spotMarketIndex]
	);

	const isMarketInReduceOnly = useMemo(
		() => ENUM_UTILS.match(spotMarketAccount?.status, MarketStatus.REDUCE_ONLY),
		[spotMarketAccount]
	);

	const marketIsProtected = useMemo(
		() => ENUM_UTILS.match(spotMarketAccount?.assetTier, AssetTier.PROTECTED),
		[spotMarketAccount]
	);

	const userHasAccountInPool = useMemo(
		() => subaccounts.some((acc) => acc?.poolId === spotMarketAccount?.poolId),
		[subaccounts, spotMarketAccount?.poolId]
	);

	if (!spotMarketInfo) return null;

	const borrowDisabled =
		marketIsProtected || !userHasAccountInPool || isPrivyNativeAuth;
	const isBorrow = spotMarketInfo.borrowsBase.gtZero();
	const hasUserSpotBalance = isBorrow
		? spotMarketInfo.borrowsBase.gtZero()
		: spotMarketInfo.depositsBase.gtZero();

	const displayWithdrawButton = hasUserSpotBalance && !isBorrow;
	const displayRepayButton = hasUserSpotBalance && isBorrow;
	const displayDepositButton = !isMarketInReduceOnly && !displayRepayButton;
	const displayBorrowButton =
		!borrowDisabled && !isMarketInReduceOnly && !displayWithdrawButton;

	return (
		<div className="flex w-full items-center gap-sm">
			{displayDepositButton && (
				<Button
					size="sm"
					variant="secondary"
					className="flex-1"
					onClick={() => {
						show({
							type: 'deposit',
							props: { collateralIndex: spotMarketIndex },
						});
					}}
					dataTestId="balance-row-deposit"
				>
					Deposit
				</Button>
			)}
			{displayRepayButton && (
				<Button
					size="sm"
					variant="secondary"
					className="flex-1"
					onClick={() => {
						show({
							type: 'repay',
							props: { collateralIndex: spotMarketIndex },
						});
					}}
					dataTestId="balance-row-repay"
				>
					Repay
				</Button>
			)}
			{displayWithdrawButton && (
				<Button
					size="sm"
					variant="secondary"
					className="flex-1"
					onClick={() => {
						show({
							type: 'withdraw',
							props: { collateralIndex: spotMarketIndex },
						});
					}}
					dataTestId="balance-row-withdraw"
				>
					Withdraw
				</Button>
			)}
			{displayBorrowButton && (
				<Button
					size="sm"
					variant="secondary"
					className="flex-1"
					onClick={() => {
						show({
							type: 'borrow',
							props: { collateralIndex: spotMarketIndex },
						});
					}}
					dataTestId="balance-row-borrow"
				>
					Borrow
				</Button>
			)}
		</div>
	);
}
